import type { BackgroundExportStatus } from './backgroundExport';

export function isExportReady(status: Pick<BackgroundExportStatus, 'status' | 'download_url'>): boolean {
  return status.status === 'completed' && Boolean(status.download_url);
}

export function isExportFinalizing(status: Pick<BackgroundExportStatus, 'status' | 'download_url'>): boolean {
  return status.status === 'completed' && !status.download_url;
}

export function getDisplayExportStatus(status: Pick<BackgroundExportStatus, 'status' | 'download_url'>): BackgroundExportStatus['status'] | 'finalizing' {
  if (isExportFinalizing(status)) return 'finalizing';
  return status.status;
}

export function isExportActive(status: Pick<BackgroundExportStatus, 'status' | 'download_url'>): boolean {
  const displayStatus = getDisplayExportStatus(status);
  return displayStatus === 'queued' || displayStatus === 'processing' || displayStatus === 'combining' || displayStatus === 'finalizing';
}

const STEP_LABELS: Record<string, string> = {
  queued_for_background_processing: 'Queued for processing',
  fetching_data: 'Fetching report data',
  creating_csv_chunks: 'Preparing export',
  processing_chunks: 'Processing export',
  combining_chunks: 'Finalizing export file',
  signing_download_url: 'Preparing download link',
  download_ready: 'Ready for download',
  completed: 'Completed',
};

const INTERNAL_STEP_EXACT = new Set([
  'all_chunks_completed',
]);

export function getDisplayStepMessage(step?: string | null): string | undefined {
  if (!step) return undefined;
  const normalized = step.trim().toLowerCase();
  if (!normalized || INTERNAL_STEP_EXACT.has(normalized)) return undefined;
  return STEP_LABELS[normalized] ?? 'Processing...';
}

export function getDisplayErrorMessage(error?: string | null): string | undefined {
  if (!error) return undefined;
  const normalized = error.trim().toLowerCase();
  if (!normalized) return undefined;
  if (
    normalized.includes('edge function returned a non-2xx status code') ||
    normalized.includes('all_chunks_completed')
  ) {
    return 'Export failed. Please try again.';
  }
  return error;
}
